/**
 * 컨테이너 DOM에서 임의 텍스트를 찾아 하이라이트하고 뷰로 스크롤한다.
 *
 * `ContentSearchBar`는 검색어를 외부에서 주입할 수 없어(내부 state 전용) 재사용이 불가하다.
 * 그래서 인용문 클릭 → 오른쪽 허가사항 원문 하이라이트(계획 D3)를 위해
 * TreeWalker로 대상 컨테이너를 훑는 별도 유틸을 둔다. 신규 "컴포넌트"가 아니라 함수다.
 *
 * 원문 텍스트는 DOM 노드 경계·공백에서 어긋날 수 있어 정확 매칭이 어렵다.
 * 단일 텍스트 노드 내 정규화 부분일치를 우선하고, 실패 시 앞부분 일부로 재시도하는
 * best-effort 구현이다(실 응답 확인은 백엔드 배포 후 — 계획 §7 리스크 1·2).
 */

const HIGHLIGHT_CLASS = "dose-rule-citation-highlight";

/** 비교용 공백 정규화(연속 공백 1칸, 앞뒤 트림). */
const normalize = (text: string): string => text.replace(/\s+/g, " ").trim();

/** 컨테이너에서 이전 하이라이트를 모두 제거한다(span 언랩). */
export const clearCitationHighlights = (container: HTMLElement | null): void => {
  if (!container) return;
  const marks = container.querySelectorAll(`.${HIGHLIGHT_CLASS}`);
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
    parent.normalize();
  });
};

/** 텍스트 노드 하나에서 needle의 시작 인덱스를 정규화 비교로 찾는다(없으면 -1). */
const findInNode = (nodeText: string, needle: string): number => {
  const rawIdx = nodeText.indexOf(needle);
  if (rawIdx >= 0) return rawIdx;
  // 정규화 비교(공백 차이 흡수) — 원문 인덱스 복원은 근사치로 처리.
  const normNode = normalize(nodeText);
  const normNeedle = normalize(needle);
  if (!normNeedle) return -1;
  const normIdx = normNode.indexOf(normNeedle);
  if (normIdx < 0) return -1;
  // 근사: 정규화 위치에 대응하는 원문 위치를 앞에서부터 되짚는다.
  let seen = 0;
  for (let i = 0; i < nodeText.length; i += 1) {
    if (seen === normIdx) return i;
    if (!/\s/.test(nodeText[i]) || (i > 0 && !/\s/.test(nodeText[i - 1]))) {
      seen += 1;
    }
  }
  return -1;
};

/** 하이라이트 span으로 감싼 뒤 뷰로 스크롤한다. */
const wrapAndScroll = (textNode: Text, start: number, length: number): void => {
  const range = document.createRange();
  range.setStart(textNode, start);
  range.setEnd(textNode, Math.min(start + length, textNode.length));
  const mark = document.createElement("span");
  mark.className = HIGHLIGHT_CLASS;
  try {
    range.surroundContents(mark);
  } catch {
    // surroundContents가 경계를 넘으면 실패 — 스크롤만 시도.
    textNode.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }
  mark.scrollIntoView({ behavior: "smooth", block: "center" });
};

/**
 * 컨테이너에서 text를 찾아 하이라이트+스크롤. 성공하면 true.
 * 이전 하이라이트는 먼저 지운다.
 */
export const highlightTextInContainer = (
  container: HTMLElement | null,
  text: string | null | undefined
): boolean => {
  if (!container || !text) return false;
  const needle = text.trim();
  if (!needle) return false;

  clearCitationHighlights(container);

  // 매칭 시도 순서: 전체 → 앞 40자 → 앞 20자(원문/DOM 어긋남 대비).
  const candidates = [needle];
  if (needle.length > 40) candidates.push(needle.slice(0, 40));
  if (needle.length > 20) candidates.push(needle.slice(0, 20));

  for (const candidate of candidates) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode() as Text | null;
    while (node) {
      const idx = findInNode(node.textContent ?? "", candidate);
      if (idx >= 0) {
        wrapAndScroll(node, idx, candidate.length);
        return true;
      }
      node = walker.nextNode() as Text | null;
    }
  }
  return false;
};
